import { Button, Dialog, DialogActions, DialogContent, Divider, FormControlLabel, FormGroup, List, ListItem, ListItemText, Switch, TextField, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import { Alert } from "@mui/lab";
import * as api from "api/Core";
import { FormStyleClasses } from "assets/jss/common-styles";
import { RequestIndicator } from "components/CallServer";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import MuiDanger from "components/MuiExtras/Typography/MuiDanger";
import MuiSuccess from "components/MuiExtras/Typography/MuiSuccess";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import ReactComment from "components/ReactComment";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { ImportUsersResultDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    onClose: () => void;
}

interface IState {
    formCycle: RequestLifeCycle,
    csv: string;
    isPublisher: boolean;
    isPublisherVideo: boolean;
    importResult: ImportUsersResultDto;
}

type Props = IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class UserImportDialog extends React.Component<Props, IState> {

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            formCycle: RequestLifeCycle.None,
            csv: "",
            isPublisher: false,
            isPublisherVideo: false,
            importResult: null
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.User.Dialogs.UserImport." + r, f);
    }

    ///
    private isValidForm(): boolean {
        return this.state.csv != "";
    }

    ///
    private renderForm() {
        return (
            <form noValidate autoComplete="nope" >
                {/* Truque para impedir autocomplete */}
                <ReactComment text="<input type='password' autocomplete='new-password' />" />

                <FormGroup>
                    <TextField
                        variant="outlined"
                        multiline
                        autoFocus={true}
                        fullWidth={true}
                        rows={10}
                        autoComplete="off"
                        placeholder={this.res("InputPlaceholder1") + '\n' + this.res("InputPlaceholder2")}
                        onChange={
                            (e) => {
                                this.setState({ csv: e.target.value });
                            }
                        }
                    />
                </FormGroup>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                name="isPublisher"
                                checked={this.state.isPublisher}
                                onChange={
                                    (e) => {
                                        const checked = (e.target as HTMLInputElement).checked;
                                        this.setState({
                                            isPublisher: checked,
                                            isPublisherVideo: checked == false ? false : this.state.isPublisherVideo,
                                        });
                                    }
                                }
                                value="isPublisher"
                                color="secondary"
                            />
                        }
                        label={this.res("PublisherLabel")}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                name="isPublisherVideo"
                                checked={this.state.isPublisherVideo}
                                onChange={
                                    (e) => {
                                        const checked = (e.target as HTMLInputElement).checked;
                                        this.setState({
                                            isPublisher: checked == true ? true : this.state.isPublisher,
                                            isPublisherVideo: checked
                                        });
                                    }
                                }
                                value="isPublisherVideo"
                                color="secondary"
                            />
                        }
                        label={this.res("PublisherVideoLabel")}
                    />
                </FormGroup>

                <Alert severity="warning">{this.res("FormNotes")}</Alert>
            </form>
        );
    }

    private renderResults() {
        const { importResult } = this.state;
        return (
            <div>
                <MuiContainer>

                    <MuiContainer>
                        <Typography variant="button">{this.res("ResultTitle")}</Typography>
                        <div><MuiSuccess>{this.res("ResultSuccessUsers", { count: importResult.successCount })}</MuiSuccess></div>
                        <div><MuiDanger>{this.res("ResultFailedUsers", { count: importResult.errors.length })}</MuiDanger></div>
                    </MuiContainer>

                    {
                        importResult.licensedBlocked > 0 &&
                        <Alert severity="warning">
                            {this.res("ResultNoLicensedUsers", { count: importResult.licensedBlocked })}
                        </Alert>
                    }
                </MuiContainer>

                {
                    importResult.errors.length > 0 &&
                    <div>
                        <Typography variant="button">{this.res("ErrorListTitle")}:</Typography>
                        <MuiPaper paperColor="mutedLight" >
                            <List>
                                {
                                    importResult.errors.map((v) => (
                                        <>
                                            <ListItem>
                                                <ListItemText
                                                    primary={v.line}
                                                    secondary={
                                                        <Typography component="span" variant="body2" color="error">
                                                            {v.message}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider component="li" />
                                        </>
                                    ))
                                }
                            </List>
                        </MuiPaper>
                    </div>
                }
            </div>
        );
    }


    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (this.isValidForm()) {

            this.setState({
                formCycle: RequestLifeCycle.Posting,
            });

            api.importUsers(this.state.csv, this.state.isPublisher, this.state.isPublisherVideo)
                .then((result) => {
                    this.setState({ formCycle: RequestLifeCycle.None, importResult: result });
                })
                .catch((err: any) => {
                    this.setState({ formCycle: RequestLifeCycle.None });
                });
        }
    }

    ///
    render() {
        return (
            <Dialog
                open
                fullWidth
                maxWidth="md"
                onClose={
                    (even, reason) => {
                        if (reason !== 'backdropClick') {
                            this.props.onClose();
                        }
                    }
                }
            >
                <MuiDialogTitleClose onClose={this.props.onClose}
                    title={this.res("BoxTitle")}
                    subtitle={this.res("BoxSubtitle")}
                />


                <DialogContent>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    {
                        this.state.importResult == null
                            ? this.renderForm()
                            : this.renderResults()
                    }

                </DialogContent>

                <DialogActions>
                    <MuiPushRight>
                        <Button
                            color="primary"
                            size="large"
                            variant="contained"
                            component="span"
                            disabled={!this.isValidForm()}
                            onClick={
                                (e: React.MouseEvent<HTMLButtonElement>) => {
                                    if (this.state.importResult == null)
                                        this.handleSubmit(e);
                                    else
                                        this.props.onClose();
                                }
                            }
                        >
                            <span>
                                {
                                    this.state.importResult == null
                                        ? this.props.t("Common.Buttons.ConfirmText")
                                        : this.props.t("Common.Buttons.CloseText")
                                }
                            </span>
                        </Button>
                    </MuiPushRight>
                </DialogActions>
            </Dialog>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(UserImportDialog)));